// ─────────────────────────────────────────────────────────────────────────────
// MovementComponent
//
// Componente de movimento GENÉRICO e DESACOPLADO. Recebe um perfil (do JSON)
// e executa o deslocamento no `update(dt)` do loop principal — sem NENHUMA
// referência ao jogador. Serve para NPCs, patrulheiros e inimigos ambientais.
//
// Não desenha nada: expõe `position`, `velocity`, `moving` e `getFrame(...)`.
// O renderer da sua engine lê esses valores e desenha o billboard/sprite.
// ─────────────────────────────────────────────────────────────────────────────

import {
  DIR8_TO_GRID,
  dirToRow,
  resolveSpriteFrame,
  SpriteFrame,
  velocityToDir,
} from './directions';
import {
  ActionAnimation,
  AnimationConfig,
  DEFAULT_ROW_ORDER,
  Dir8,
  LoopMode,
  MoveSpace,
  NpcProfile,
  Waypoint,
} from './schema';

export interface Vec2 { x: number; y: number; }

export class MovementComponent {
  readonly id: string;
  readonly space: MoveSpace;

  /** Posição atual, na unidade do perfil (`tile` = célula, `screen` = px). */
  position: Vec2;
  /** Vetor de velocidade do frame atual (mesma unidade / segundo). */
  velocity: Vec2 = { x: 0, y: 0 };
  /** true enquanto se desloca; false enquanto espera num waypoint / está static. */
  moving = false;

  private profileType: 'static' | 'patrol';
  private anim?: AnimationConfig;

  // ── estado de patrulha ──
  private waypoints: Waypoint[] = [];
  private loopMode: LoopMode = 'loop';
  private speed = 0;
  private waitDefault = 0;
  private arriveEps = 0.05;
  private targetIndex = 0;
  private pingDir: 1 | -1 = 1;
  private waitTimer = 0;

  // ── estado de animação ──
  private walkElapsed = 0;
  private lastDir: Dir8;
  /**
   * Orientação atual como VETOR DE GRADE (estável sob rotação de câmera).
   * Atualizado a cada passo de deslocamento; usado para re-derivar a direção
   * de tela mesmo com a entidade parada — assim ela vira junto com a câmera.
   */
  private lastMoveVec: Vec2;

  // ── estado de ação (forjar / atacar…) ──
  private actions: Record<string, ActionAnimation> = {};
  private currentAction: string | null = null;
  private actionElapsed = 0;

  constructor(id: string, profile: NpcProfile, startPos?: Vec2) {
    this.id = id;
    this.profileType = profile.type;
    this.anim = profile.animation;
    this.actions = profile.animation?.actions ?? {};
    this.lastDir = (profile.animation?.restFacing)
      ?? (profile.type === 'static' ? (profile.facing ?? 'S') : 'S');
    this.lastMoveVec = { ...DIR8_TO_GRID[this.lastDir] };

    if (profile.type === 'static') {
      this.space = 'screen';
      this.position = startPos ?? { x: 0, y: 0 };
      return;
    }

    this.space = profile.space ?? 'tile';
    this.loopMode = profile.loopMode ?? 'loop';
    this.speed = profile.speed;
    this.waitDefault = profile.waitTimeDefault ?? 0;
    this.arriveEps = profile.arriveEpsilon ?? (this.space === 'tile' ? 0.05 : 2);

    // waypoints relativos: somados à posição inicial informada no registro.
    const useRelative = !!(profile.relativeWaypoints && startPos);
    this.waypoints = useRelative
      ? profile.waypoints.map(w => ({ ...w, x: startPos!.x + w.x, y: startPos!.y + w.y }))
      : profile.waypoints;

    const startIndex = Math.min(profile.startIndex ?? 0, this.waypoints.length - 1);
    const wp = this.waypoints[startIndex];
    this.position = (startPos && !useRelative) ? { x: startPos.x, y: startPos.y } : { x: wp.x, y: wp.y };
    // começa mirando o PRÓXIMO waypoint
    this.targetIndex = startIndex;
    this.advanceTarget();
  }

  /** Faz o alvo avançar segundo o `loopMode`. */
  private advanceTarget(): void {
    const n = this.waypoints.length;
    if (n <= 1) { this.targetIndex = 0; return; }

    if (this.loopMode === 'loop') {
      this.targetIndex = (this.targetIndex + 1) % n;
      return;
    }
    if (this.loopMode === 'once') {
      this.targetIndex = Math.min(this.targetIndex + 1, n - 1);
      return;
    }
    // pingpong
    let next = this.targetIndex + this.pingDir;
    if (next >= n) { this.pingDir = -1; next = n - 2; }
    else if (next < 0) { this.pingDir = 1; next = 1; }
    this.targetIndex = next;
  }

  /** Avança o componente em `dt` segundos. */
  update(dt: number): void {
    if (this.profileType === 'static' || this.waypoints.length === 0) {
      this.velocity = { x: 0, y: 0 };
      this.moving = false;
      return;
    }

    // esperando num waypoint?
    if (this.waitTimer > 0) {
      this.waitTimer = Math.max(0, this.waitTimer - dt);
      this.velocity = { x: 0, y: 0 };
      this.moving = false;
      if (this.currentAction) this.actionElapsed += dt;
      if (this.waitTimer === 0) {
        this.currentAction = null;
        this.advanceTarget();
      }
      return;
    }

    const target = this.waypoints[this.targetIndex];
    const dx = target.x - this.position.x;
    const dy = target.y - this.position.y;
    const dist = Math.hypot(dx, dy);

    // 'once' terminado: para de vez no último ponto
    if (this.loopMode === 'once'
      && this.targetIndex === this.waypoints.length - 1
      && dist <= this.arriveEps) {
      this.position = { x: target.x, y: target.y };
      this.velocity = { x: 0, y: 0 };
      this.moving = false;
      return;
    }

    if (dist <= this.arriveEps || dist === 0) {
      // chegou: snap + inicia espera (ou avança se sem espera)
      this.position = { x: target.x, y: target.y };
      const wait = target.wait ?? this.waitDefault;
      this.velocity = { x: 0, y: 0 };
      this.moving = false;
      if (wait > 0) {
        this.waitTimer = wait;
        // inicia a ação vinculada a este ponto (se existir e for válida)
        const actName = target.action;
        if (actName && this.actions[actName]) {
          this.currentAction = actName;
          this.actionElapsed = 0;
          const facing = this.actions[actName].facing;
          if (facing) this.lastDir = facing;
        } else {
          this.currentAction = null;
        }
      } else {
        this.advanceTarget();
      }
      return;
    }

    // desloca em direção ao alvo, sem ultrapassar
    const stepLen = Math.min(this.speed * dt, dist);
    const nx = dx / dist;
    const ny = dy / dist;
    this.position = {
      x: this.position.x + nx * stepLen,
      y: this.position.y + ny * stepLen,
    };
    this.velocity = { x: nx * this.speed, y: ny * this.speed };
    this.moving = true;
    this.walkElapsed += dt;

    // memoriza a direção enquanto se move (usada depois, parado). Guarda tanto
    // o nome (fallback p/ sets 'screen*') quanto o vetor de grade (p/ 'iso8'
    // continuar girando com a câmera mesmo parado).
    this.lastMoveVec = { x: nx, y: ny };
    if (this.anim) {
      this.lastDir = velocityToDir(
        this.velocity.x, this.velocity.y,
        this.anim.directionSet ?? 'iso8',
        0, this.lastDir,
      );
    }
  }

  /**
   * Frame do spritesheet a desenhar agora.
   * @param cameraAngle rad — só afeta `directionSet: 'iso8'`. Passe 0 se a
   *                    câmera não gira ou se você quer a direção travada.
   */
  getFrame(cameraAngle = 0): SpriteFrame | null {
    if (!this.anim) return null;

    // Ação em andamento (forjar/atacar): ciclo próprio de colunas, direção
    // travada. Ignora `moving`/`walkElapsed`.
    const act = this.currentAction ? this.actions[this.currentAction] : null;
    if (act) {
      const set = this.anim.directionSet ?? 'iso8';
      const rowOrder = this.anim.rowOrder ?? DEFAULT_ROW_ORDER;
      // vetor de grade que a ação encara: `facing` explícito, senão a última
      // direção de deslocamento (rumo ao ponto da ação). Gira com a câmera.
      const faceVec = act.facing ? DIR8_TO_GRID[act.facing] : this.lastMoveVec;
      const facing = set === 'iso8'
        ? velocityToDir(faceVec.x, faceVec.y, 'iso8', cameraAngle, this.lastDir)
        : (act.facing ?? this.lastDir);
      const maxRow = Math.max(0, this.anim.rows - 1);
      const row = Math.min(act.row ?? dirToRow(facing, rowOrder), maxRow);
      const cols = act.cols.length ? act.cols : [this.anim.idleCol ?? 0];
      const fps = act.fps ?? 6;
      const idx = Math.floor(this.actionElapsed * fps) % cols.length;
      const col = Math.min(Math.max(0, cols[idx]), Math.max(0, this.anim.cols - 1));
      return { row, col, dir: facing };
    }

    return resolveSpriteFrame(
      this.anim,
      this.velocity.x, this.velocity.y,
      this.moving,
      this.walkElapsed,
      cameraAngle,
      this.lastDir,
      this.lastMoveVec,
    );
  }

  get animation(): AnimationConfig | undefined { return this.anim; }
  get facing(): Dir8 { return this.lastDir; }
  /** true enquanto uma ação (forjar/atacar) está tocando num waypoint. */
  get acting(): boolean { return this.currentAction !== null; }
  /** Nome da ação em andamento, ou null. */
  get actionName(): string | null { return this.currentAction; }
}
